import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  HabitaChartTooltipSurface,
  HabitaEmptyState,
  HabitaSectionHeader,
  HabitaViewToggle,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import type {
  CommercialInterestRegionItem,
  CommercialInterestRegionsResponse,
} from '../../../shared/models';

type InterestRegionsView = 'bars' | 'compare' | 'pie';
type InterestRegionsComparisonView = 'bars' | 'donuts';

const regionColors: Record<string, string> = {
  'Águas Lindas, GO': '#E8A33D',
  'Valparaíso de Goiás, GO': '#1F6B4F',
  'Luziânia, GO': '#E0A72E',
  'Novo Gama, GO': '#E2574C',
  'Cidade Ocidental, GO': '#3B7DD8',
  'Samambaia, DF': '#7A5CC0',
  'Anápolis, GO': '#E06A2E',
  'Sto. Antônio Descoberto, GO': '#2E9E86',
  'Outra localidade': '#4B5A52',
};

const viewOptions: Array<{
  icon: string;
  label: string;
  value: InterestRegionsView;
}> = [
  { icon: 'ri-bar-chart-horizontal-line', label: 'Barras', value: 'bars' },
  { icon: 'ri-pie-chart-line', label: 'Pizza', value: 'pie' },
  { icon: 'ri-bar-chart-grouped-line', label: 'Comparativo', value: 'compare' },
];

const comparisonViewOptions: Array<{
  icon: string;
  label: string;
  value: InterestRegionsComparisonView;
}> = [
  { icon: 'ri-bar-chart-horizontal-line', label: 'Barras agrupadas', value: 'bars' },
  { icon: 'ri-donut-chart-line', label: 'Donuts pareados', value: 'donuts' },
];

const numberFormatter = new Intl.NumberFormat('pt-BR');
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
});

function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

function formatPercent(value: number): string {
  return `${percentFormatter.format(value)}%`;
}

function regionColor(region: string): string {
  return regionColors[region] ?? regionColors['Outra localidade'];
}

function sortRegions(items: CommercialInterestRegionItem[]): CommercialInterestRegionItem[] {
  return [...items].sort((left, right) => {
    if (left.region === right.region) return 0;
    if (left.region === 'Outra localidade') return 1;
    if (right.region === 'Outra localidade') return -1;
    return right.new_leads_pct - left.new_leads_pct || left.region.localeCompare(right.region, 'pt-BR');
  });
}

interface RegionTooltipPayload {
  color?: string;
  dataKey?: string;
  name?: string;
  payload?: CommercialInterestRegionItem;
}

function RegionDistributionTooltip({
  active,
  payload,
  series,
}: {
  active?: boolean;
  payload?: RegionTooltipPayload[];
  series: 'approved' | 'leads';
}) {
  const item = payload?.[0]?.payload;
  if (!active || !item) return null;

  const isLeadSeries = series === 'leads';
  const count = isLeadSeries ? item.new_leads : item.approved_folders;
  const percentage = isLeadSeries ? item.new_leads_pct : item.approved_folders_pct;

  return (
    <HabitaChartTooltipSurface className="min-w-48">
      <p className="mb-1.5 font-semibold">{item.region}</p>
      <p className="flex items-center justify-between gap-4">
        <span className="text-textSecondary">
          {isLeadSeries ? 'Novos leads' : 'Pastas aprovadas'}
        </span>
        <strong className="font-mono">
          {formatNumber(count)} · {formatPercent(percentage)}
        </strong>
      </p>
    </HabitaChartTooltipSurface>
  );
}

function RegionComparisonTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: RegionTooltipPayload[];
}) {
  const item = payload?.[0]?.payload;
  if (!active || !item) return null;

  return (
    <HabitaChartTooltipSurface className="min-w-56">
      <p className="mb-1.5 font-semibold">{item.region}</p>
      <div className="space-y-1">
        <p className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-2 text-textSecondary">
            <span className="h-2 w-2 rounded-full bg-[#1F6B4F]" aria-hidden />
            Novos leads
          </span>
          <strong className="font-mono">
            {formatNumber(item.new_leads)} · {formatPercent(item.new_leads_pct)}
          </strong>
        </p>
        <p className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-2 text-textSecondary">
            <span className="h-2 w-2 rounded-full bg-[#E8A33D]" aria-hidden />
            Pastas aprovadas
          </span>
          <strong className="font-mono">
            {formatNumber(item.approved_folders)} · {formatPercent(item.approved_folders_pct)}
          </strong>
        </p>
      </div>
    </HabitaChartTooltipSurface>
  );
}

function RegionBars({ items }: { items: CommercialInterestRegionItem[] }) {
  const maxPercentage = Math.max(...items.map((item) => item.new_leads_pct), 1);

  return (
    <div className="space-y-4 p-4">
      {items.map((item) => (
        <div key={item.region} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-4 font-instrument text-sm">
            <span className="min-w-0 font-semibold text-textPrimary">{item.region}</span>
            <span className="shrink-0 text-xs text-textSecondary">
              <strong className="mr-1.5 text-sm text-textPrimary">{formatNumber(item.new_leads)}</strong>
              {formatPercent(item.new_leads_pct)}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-[width]"
              style={{
                backgroundColor: regionColor(item.region),
                width: `${Math.max(0, (item.new_leads_pct / maxPercentage) * 100)}%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function RegionPieLegend({
  items,
  series,
}: {
  items: CommercialInterestRegionItem[];
  series: 'approved' | 'leads';
}) {
  return (
    <div className="flex flex-wrap justify-center gap-x-2 gap-y-1.5 font-instrument text-[11px]">
      {items.map((item) => {
        const count = series === 'leads' ? item.new_leads : item.approved_folders;
        const percentage = series === 'leads' ? item.new_leads_pct : item.approved_folders_pct;
        return (
          <div
            key={item.region}
            className="flex max-w-full items-center gap-1.5 rounded border border-border-subtle px-2 py-1.5"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: regionColor(item.region) }}
              aria-hidden
            />
            <span className="min-w-0 truncate text-textSecondary">{item.region}</span>
            <span className="shrink-0 whitespace-nowrap font-mono font-semibold text-textPrimary">
              {formatNumber(count)} · {formatPercent(percentage)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RegionPieView({ items }: { items: CommercialInterestRegionItem[] }) {
  return (
    <div className="space-y-3 p-4">
      <div className="mx-auto h-64 w-full max-w-xl">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <PieChart>
            <Pie
              data={items}
              dataKey="new_leads"
              innerRadius="44%"
              nameKey="region"
              outerRadius="80%"
              paddingAngle={1}
            >
              {items.map((item) => (
                <Cell key={item.region} fill={regionColor(item.region)} />
              ))}
            </Pie>
            <Tooltip
              content={<RegionDistributionTooltip series="leads" />}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <RegionPieLegend items={items} series="leads" />
    </div>
  );
}

function RegionComparisonBars({ items }: { items: CommercialInterestRegionItem[] }) {
  const height = Math.max(340, items.length * 50);

  return (
    <div className="p-4">
      <div
        className="mb-3 flex flex-wrap justify-center gap-x-2 gap-y-1.5 font-instrument text-[11px] text-textSecondary"
        aria-label="Séries do comparativo por região"
      >
        <span className="inline-flex items-center gap-1.5 rounded border border-border-subtle px-2 py-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#1F6B4F]" aria-hidden />
          Novos leads
        </span>
        <span className="inline-flex items-center gap-1.5 rounded border border-border-subtle px-2 py-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#E8A33D]" aria-hidden />
          Pastas aprovadas
        </span>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart
            data={items}
            layout="vertical"
            margin={{ bottom: 8, left: 48, right: 108, top: 4 }}
          >
            <CartesianGrid horizontal={false} stroke="rgba(20, 70, 42, 0.12)" />
            <XAxis
              domain={[0, 'auto']}
              tick={{ fill: 'currentColor', fontSize: 10 }}
              tickFormatter={(value) => `${value}%`}
              type="number"
            />
            <YAxis
              dataKey="region"
              tick={{ fill: 'currentColor', fontSize: 11 }}
              tickLine={false}
              type="category"
              width={190}
            />
            <Tooltip
              content={<RegionComparisonTooltip />}
              cursor={{ fill: 'rgba(20, 70, 42, 0.05)' }}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
            />
            <Bar
              dataKey="new_leads_pct"
              fill="#1F6B4F"
              maxBarSize={16}
              name="Novos leads"
              radius={[0, 3, 3, 0]}
            >
              <LabelList
                dataKey="new_leads"
                formatter={(value) => formatNumber(Number(value ?? 0))}
                position="right"
              />
            </Bar>
            <Bar
              dataKey="approved_folders_pct"
              fill="#E8A33D"
              maxBarSize={16}
              name="Pastas aprovadas"
              radius={[0, 3, 3, 0]}
            >
              <LabelList
                dataKey="approved_folders"
                formatter={(value) => formatNumber(Number(value ?? 0))}
                position="right"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ComparisonDonut({
  items,
  series,
  title,
  total,
}: {
  items: CommercialInterestRegionItem[];
  series: 'approved' | 'leads';
  title: string;
  total: number;
}) {
  const dataKey = series === 'leads' ? 'new_leads' : 'approved_folders';

  return (
    <div className="min-w-0 text-center">
      <p className="font-instrument text-sm font-semibold text-textPrimary">{title}</p>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <PieChart>
            <Pie
              data={items}
              dataKey={dataKey}
              innerRadius="46%"
              nameKey="region"
              outerRadius="78%"
              paddingAngle={1}
            >
              {items.map((item) => (
                <Cell key={item.region} fill={regionColor(item.region)} />
              ))}
            </Pie>
            <Tooltip
              content={<RegionDistributionTooltip series={series} />}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 30 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="font-instrument text-xs text-textSecondary">
        <strong className="text-sm text-textPrimary">{formatNumber(total)}</strong>{' '}
        {series === 'leads' ? 'leads' : 'pastas aprovadas'}
      </p>
    </div>
  );
}

function RegionComparisonDonuts({
  approvedFolders,
  identifiedLeads,
  items,
}: {
  approvedFolders: number;
  identifiedLeads: number;
  items: CommercialInterestRegionItem[];
}) {
  return (
    <div className="p-4">
      <div className="grid gap-5 md:grid-cols-2">
        <ComparisonDonut
          items={items}
          series="leads"
          title="Novos leads"
          total={identifiedLeads}
        />
        <ComparisonDonut
          items={items}
          series="approved"
          title="Pastas aprovadas"
          total={approvedFolders}
        />
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-x-2 gap-y-1.5 border-t border-border-subtle pt-4 font-instrument text-[11px]">
        {items.map((item) => (
          <div
            key={item.region}
            className="flex max-w-full items-center gap-1.5 rounded border border-border-subtle px-2 py-1.5"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: regionColor(item.region) }}
              aria-hidden
            />
            <span className="min-w-0 truncate text-textSecondary">{item.region}</span>
            <span className="shrink-0 whitespace-nowrap font-mono font-semibold text-textPrimary">
              {formatPercent(item.new_leads_pct)} / {formatPercent(item.approved_folders_pct)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InterestRegionsCard({
  data,
  filterControl,
  filterSummary,
  isLoading,
}: {
  data: CommercialInterestRegionsResponse | null;
  filterControl: ReactNode;
  filterSummary: ReactNode;
  isLoading: boolean;
}) {
  const [view, setView] = useState<InterestRegionsView>('bars');
  const [comparisonView, setComparisonView] = useState<InterestRegionsComparisonView>('bars');
  const items = useMemo(() => sortRegions(data?.items ?? []), [data?.items]);

  const changeView = (nextView: InterestRegionsView) => {
    setView(nextView);
    if (nextView === 'compare') setComparisonView('bars');
  };

  const totalDescription = view === 'compare'
    ? `${formatNumber(data?.identified_leads ?? 0)} leads novos vs. ${formatNumber(data?.approved_folders ?? 0)} pastas aprovadas, por região.`
    : `${formatNumber(data?.identified_leads ?? 0)} leads com região identificada.`;
  const hasCoverage = Boolean(
    data && (data.total_leads > 0 || data.total_approved_folders > 0)
  );

  return (
    <section className={classNames(habitaSurfaceClass, 'order-5')}>
      <HabitaSectionHeader
        title="Principais regiões de interesse"
        description={totalDescription}
        className="px-4 py-3"
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          {filterControl}
          {view === 'compare' && (
            <HabitaViewToggle
              label="Visualização do comparativo"
              onChange={setComparisonView}
              options={comparisonViewOptions}
              value={comparisonView}
            />
          )}
          <HabitaViewToggle
            label="Visualização das regiões de interesse"
            onChange={changeView}
            options={viewOptions}
            value={view}
          />
        </div>
      </HabitaSectionHeader>
      {filterSummary}
      {hasCoverage && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-border-subtle bg-muted/25 px-4 py-2 font-instrument text-xs text-textSecondary">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-information-line text-sm text-primary" aria-hidden />
            Região identificada em{' '}
            <strong className="text-textPrimary">
              {formatNumber(data?.identified_leads ?? 0)} de {formatNumber(data?.total_leads ?? 0)} leads
            </strong>
            {' '}
            ({formatPercent(data?.lead_region_coverage_pct ?? 0)})
          </span>
          <span>
            Aprovação associada a região em{' '}
            <strong className="text-textPrimary">
              {formatNumber(data?.approved_folders ?? 0)} de {formatNumber(data?.total_approved_folders ?? 0)} pastas
            </strong>
            {' '}
            ({formatPercent(data?.approval_region_coverage_pct ?? 0)})
          </span>
        </div>
      )}

      {isLoading && !data ? (
        <div className="flex min-h-64 items-center justify-center gap-2 font-instrument text-sm text-textSecondary">
          <i className="ri-loader-4-line animate-spin text-lg" aria-hidden />
          Carregando regiões de interesse...
        </div>
      ) : items.length === 0 ? (
        <div className="p-4">
          <HabitaEmptyState
            icon="ri-map-pin-line"
            title="Sem regiões identificadas"
            description="Não há eventos de localidade consolidados para os filtros atuais."
          />
        </div>
      ) : view === 'bars' ? (
        <RegionBars items={items} />
      ) : view === 'pie' ? (
        <RegionPieView items={items} />
      ) : comparisonView === 'bars' ? (
        <RegionComparisonBars items={items} />
      ) : (
        <RegionComparisonDonuts
          approvedFolders={data?.approved_folders ?? 0}
          identifiedLeads={data?.identified_leads ?? 0}
          items={items}
        />
      )}
    </section>
  );
}
